import { feature } from 'topojson-client';
import JSZip from 'jszip';
import * as turf from '@turf/turf';
import { kml } from '@tmcw/togeojson';

/**
 * Convert TopoJSON to GeoJSON
 */
export const topoJsonToGeoJson = (topojson: any): any => {
  if (!topojson.objects) {
    throw new Error('Invalid TopoJSON: missing objects property');
  }

  const objectName = Object.keys(topojson.objects)[0];
  return feature(topojson, topojson.objects[objectName]);
};

/**
 * Extract and parse KMZ file (which is a zipped KML)
 */
export const extractKmz = async (file: File): Promise<string> => {
  const zip = new JSZip();
  const contents = await zip.loadAsync(file);

  const kmlFile = Object.keys(contents.files).find((name) =>
    name.toLowerCase().endsWith('.kml')
  );

  if (!kmlFile) {
    throw new Error('No KML file found in KMZ archive');
  }

  return await contents.files[kmlFile].async('string');
};

/**
 * Parse KML string to GeoJSON using @tmcw/togeojson
 */
export const kmlToGeoJson = (kmlString: string): any => {
  try {
    const parser = new DOMParser();
    const kmlDoc = parser.parseFromString(kmlString, 'text/xml');

    const parserError = kmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('Invalid KML: XML parsing failed');
    }

    const geojson = kml(kmlDoc);

    if (!geojson || !geojson.features || geojson.features.length === 0) {
      throw new Error('No features found in KML file');
    }

    return geojson;
  } catch (error) {
    throw new Error(
      `KML conversion failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/**
 * Validate GeoJSON geometry
 */
export const validateGeometry = (geojson: any): { valid: boolean; error?: string } => {
  if (!geojson) {
    return { valid: false, error: 'No geometry provided' };
  }

  if (typeof geojson !== 'object') {
    return { valid: false, error: 'Geometry must be an object' };
  }

  if (geojson.type === 'FeatureCollection') {
    if (!Array.isArray(geojson.features)) {
      return { valid: false, error: 'FeatureCollection must have features array' };
    }
    if (geojson.features.length === 0) {
      return { valid: false, error: 'FeatureCollection has no features' };
    }
    return validateGeometry(geojson.features[0].geometry);
  }

  if (geojson.type === 'Feature') {
    return validateGeometry(geojson.geometry);
  }

  const validTypes = [
    'Point',
    'MultiPoint',
    'LineString',
    'MultiLineString',
    'Polygon',
    'MultiPolygon',
    'GeometryCollection',
  ];

  if (!validTypes.includes(geojson.type)) {
    return { valid: false, error: `Invalid geometry type: ${geojson.type}` };
  }

  if (!geojson.coordinates && geojson.type !== 'GeometryCollection') {
    return { valid: false, error: 'Geometry must have coordinates' };
  }

  return { valid: true };
};

/**
 * Extract geometry from GeoJSON (handles FeatureCollection, Feature, and Geometry)
 */
export const extractGeometry = (geojson: any): any => {
  if (!geojson) return null;

  if (geojson.type === 'FeatureCollection') {
    return geojson.features[0]?.geometry || null;
  }

  if (geojson.type === 'Feature') {
    return geojson.geometry;
  }

  return geojson;
};

/**
 * Format geometry for display in JSON editor
 */
export const formatGeometryForEditor = (geometry: any): string => {
  if (!geometry) return '';
  return JSON.stringify(geometry, null, 2);
};

/**
 * Parse geometry from JSON editor
 */
export const parseGeometryFromEditor = (text: string): { geometry: any; error?: string } => {
  try {
    const parsed = JSON.parse(text);
    const validation = validateGeometry(parsed);

    if (!validation.valid) {
      return { geometry: null, error: validation.error };
    }

    return { geometry: extractGeometry(parsed) };
  } catch (error) {
    return {
      geometry: null,
      error: `Invalid JSON: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
};

/**
 * Detect file format from filename
 */
export const detectFileFormat = (
  filename: string
): 'geojson' | 'topojson' | 'kmz' | 'kml' | 'unknown' => {
  const lower = filename.toLowerCase();
  if (lower.endsWith('.geojson') || lower.endsWith('.json')) return 'geojson';
  if (lower.endsWith('.topojson')) return 'topojson';
  if (lower.endsWith('.kmz')) return 'kmz';
  if (lower.endsWith('.kml')) return 'kml';
  return 'unknown';
};

/**
 * Process uploaded file and convert to GeoJSON geometry
 */
export const processGeometryFile = async (
  file: File
): Promise<{ geometry: any; error?: string }> => {
  const format = detectFileFormat(file.name);

  try {
    if (format === 'geojson') {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const validation = validateGeometry(parsed);

      if (!validation.valid) {
        return { geometry: null, error: validation.error };
      }

      return { geometry: extractGeometry(parsed) };
    }

    if (format === 'topojson') {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const geojson = topoJsonToGeoJson(parsed);
      return { geometry: extractGeometry(geojson) };
    }

    if (format === 'kmz') {
      const kmlText = await extractKmz(file);
      const geojson = kmlToGeoJson(kmlText);
      return { geometry: extractGeometry(geojson) };
    }

    if (format === 'kml') {
      const kmlText = await file.text();
      const geojson = kmlToGeoJson(kmlText);
      return { geometry: extractGeometry(geojson) };
    }

    return { geometry: null, error: `Unsupported file format: ${file.name}` };
  } catch (error) {
    return {
      geometry: null,
      error: `Error processing file: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
};

/**
 * Interface for drawn features used in DrawingModal
 */
export interface DrawnFeature {
  id: string;
  type: 'Point' | 'LineString' | 'Polygon' | 'Circle' | 'Rectangle';
  geometry: any;
  label: string;
  createdAt: number;
}

/**
 * Merge multiple drawn features into a single geometry
 */
export const mergeGeometries = (features: DrawnFeature[]): any => {
  if (features.length === 0) return null;
  if (features.length === 1) return features[0].geometry;

  const normalizeType = (type: string) => {
    if (type === 'Circle' || type === 'Rectangle') return 'Polygon';
    return type;
  };

  const typeGroups: Record<string, DrawnFeature[]> = {};
  features.forEach((f) => {
    const normalized = normalizeType(f.type);
    if (!typeGroups[normalized]) {
      typeGroups[normalized] = [];
    }
    typeGroups[normalized].push(f);
  });

  const types = Object.keys(typeGroups);

  if (types.length === 1) {
    const type = types[0];
    if (type === 'Point' || type === 'LineString' || type === 'Polygon') {
      return {
        type: `Multi${type}`,
        coordinates: typeGroups[type].map((f) => f.geometry.coordinates),
      };
    }
  }

  return {
    type: 'GeometryCollection',
    geometries: features.map((f) => f.geometry),
  };
};

/**
 * Split a geometry into individual editable features
 */
export const splitGeometry = (geometry: any): DrawnFeature[] => {
  if (!geometry) return [];

  const features: DrawnFeature[] = [];
  let counter = 1;

  const createFeature = (geom: any, type: string): DrawnFeature => ({
    id: crypto.randomUUID(),
    type: type as any,
    geometry: geom,
    label: `${type} #${counter++}`,
    createdAt: Date.now(),
  });

  if (geometry.type === 'GeometryCollection') {
    geometry.geometries.forEach((geom: any) => {
      features.push(...splitGeometry(geom));
    });
    return features;
  }

  if (geometry.type.startsWith('Multi')) {
    const baseType = geometry.type.replace('Multi', '');
    geometry.coordinates.forEach((coords: any) => {
      features.push(
        createFeature(
          {
            type: baseType,
            coordinates: coords,
          },
          baseType
        )
      );
    });
    return features;
  }

  features.push(createFeature(geometry, geometry.type));
  return features;
};

/**
 * Calculate area of a geometry in square meters
 */
export const calculateArea = (geometry: any): number => {
  if (!geometry) return 0;

  try {
    let geom = geometry;
    if (geometry.type === 'Feature') {
      geom = geometry.geometry;
    }

    const feat = geom.type === 'Feature' ? geom : turf.feature(geom);
    return turf.area(feat);
  } catch (error) {
    console.error('[calculateArea] ERROR:', error);
    return 0;
  }
};

/**
 * Extract radius from a circle geometry
 */
export const calculateRadius = (geometry: any): number | null => {
  if (!geometry || geometry.type !== 'Polygon') return null;

  try {
    const center = turf.center(geometry);
    const coords = geometry.coordinates[0];
    if (!coords || coords.length === 0) return null;

    const firstPoint = turf.point(coords[0]);
    return turf.distance(center, firstPoint, { units: 'meters' });
  } catch (error) {
    console.error('Error calculating radius:', error);
    return null;
  }
};

/**
 * Buffer a LineString geometry to create a corridor polygon
 */
export const bufferLineString = (lineGeometry: any, widthMeters: number): any => {
  if (!lineGeometry || lineGeometry.type !== 'LineString') {
    throw new Error('Invalid geometry: must be a LineString');
  }

  try {
    const radiusKm = widthMeters / 2 / 1000;
    const buffered = turf.buffer(lineGeometry, radiusKm, { units: 'kilometers' });
    if (!buffered) {
      throw new Error('Failed to buffer line geometry');
    }
    return buffered.geometry;
  } catch (error) {
    console.error('Error buffering line:', error);
    throw error;
  }
};
